import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '../lib/api.js';
import { DestinationFeature } from '@mpr/shared';
import { DestinationFilter } from './DestinationPicker.js';
import { pageDestinationId, pageIdOf } from '../lib/destinations.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import { ErrorNotice } from './ErrorNotice.js';
import { Icon } from './Icons.js';

/**
 * What other people said about a Page.
 *
 * Two things Facebook keeps in separate places and a Page owner thinks of as
 * one question: posts that tagged the Page, and recommendations left on it.
 *
 * Read-only on purpose. Replying to a mention means posting on someone else's
 * post, which is not something this app does — it acts on Pages you administer
 * and nowhere else.
 */
export function MentionsPanel() {
  const { t, language } = useI18n();
  const locale = language === 'ar' ? 'ar' : 'en';
  const [pageId, setPageId] = useState('');

  const data = useQuery({
    queryKey: ['page-mentions', pageId],
    queryFn: () => api.pageMentions(pageId),
    enabled: pageId !== '',
  });

  const when = (iso: string) => new Date(iso).toLocaleDateString(locale);
  const mentions = data.data?.mentions ?? [];
  const reviews = data.data?.reviews ?? [];

  return (
    <section className="panel">
      <h2>{t('mentions.title')}</h2>
      <p className="muted small">{t('mentions.subtitle')}</p>

      <label htmlFor="mentions-page">{t('mentions.selectPage')}</label>
      <DestinationFilter
        id="mentions-page"
        feature={DestinationFeature.MENTIONS}
        required
        value={pageId ? pageDestinationId(pageId) : ''}
        allLabel={t('mentions.selectPagePlaceholder')}
        onChange={(id) => setPageId(pageIdOf(id))}
      />

      {data.isError && <ErrorNotice error={data.error} />}
      {data.isLoading && pageId && <div className="loading">{t('common.loading')}</div>}

      {data.data && (
        <>
          <h3 className="mentions-head">
            <Icon.comment /> {t('mentions.mentions')} ({mentions.length})
          </h3>

          {mentions.length === 0 ? (
            <p className="muted small">{t('mentions.noMentions')}</p>
          ) : (
            <ul className="mention-list">
              {mentions.map((mention) => (
                <li key={mention.id}>
                  <div className="mention-body">
                    <span className="mention-who">
                      {/* Meta withholds the author for anyone who has not
                          authorised this app, which is nearly everyone. Saying
                          "someone" is honest; inventing a name would not be. */}
                      {mention.authorName ?? t('mentions.someone')}
                      <span className="muted small"> · {when(mention.createdTime)}</span>
                    </span>
                    <p>{mention.message ?? t('common.noText')}</p>
                  </div>
                  {mention.permalink && (
                    <a
                      href={mention.permalink}
                      target="_blank"
                      rel="noreferrer"
                      className="ghost small"
                    >
                      {t('mentions.open')}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}

          <h3 className="mentions-head">
            <Icon.check /> {t('mentions.reviews')} ({reviews.length})
          </h3>

          {reviews.length === 0 ? (
            <p className="muted small">{t('mentions.noReviews')}</p>
          ) : (
            <ul className="mention-list">
              {reviews.map((review, index) => (
                <li key={index}>
                  <span className={review.positive ? 'badge ok' : 'badge warn'}>
                    {review.positive ? t('mentions.recommends') : t('mentions.doesNot')}
                  </span>
                  <div className="mention-body">
                    <span className="mention-who">
                      {review.reviewerName ?? t('mentions.someone')}
                      <span className="muted small"> · {when(review.createdTime)}</span>
                    </span>
                    {review.text && <p>{review.text}</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
